import React from 'react';
import useGameStore from '../../store/gameStore';
import { Handshake, Lock, Check, X, Clock } from 'lucide-react';
import { toast } from '../ui/sonner';
import { formatMoney } from '../../utils/format';
import { etatFacingNegocie, impactOffre } from '../../utils/facingNegocie';

const delta = (a, b, suffix = '') => {
  const d = Math.round((b - a) * 10) / 10;
  return <span className={d > 0 ? 'text-emerald-400' : d < 0 ? 'text-red-400' : 'text-slate-400'}>{d > 0 ? '+' : ''}{d}{suffix}</span>;
};

/** Offres de tête de gondole payées par les fournisseurs + contrats en cours (secteur courant). */
export const OffresFacingPanel = ({ secteurId }) => {
  const retail = useGameStore(s => s.retail);
  const month = useGameStore(s => s.month) || 1;
  const totalDays = useGameStore(s => s.totalDays) || 0;
  const accepter = useGameStore(s => s.accepterOffreFacing);
  const refuser = useGameStore(s => s.refuserOffreFacing);
  const etat = etatFacingNegocie(retail);
  const offres = etat.offres.filter(o => o.secteurId === secteurId);
  const contrats = etat.contrats.filter(c => c.secteurId === secteurId);
  if (!offres.length && !contrats.length) return null;

  return (
    <div className="surface-card p-3 space-y-3" data-testid="offres-facing-panel">
      <div className="flex items-center gap-2">
        <Handshake className="w-4 h-4 text-amber-300" />
        <p className="text-[10px] uppercase tracking-widest text-slate-400">Têtes de gondole négociées avec les fournisseurs</p>
        {etat.cumulEur > 0 && <span className="ml-auto text-[10px] font-mono text-emerald-300" data-testid="offres-facing-cumul">{formatMoney(etat.cumulEur)} encaissés</span>}
      </div>

      {offres.map(o => {
        const imp = impactOffre(retail, o, month);
        return (
          <div key={o.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid={`offre-facing-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-100">{o.fournisseurNom} propose {o.facings} facings <span className="text-amber-200">{o.famNom}</span> au niveau des yeux</p>
                <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {o.dureeJours} jours · offre valable encore {Math.max(0, o.expireJour - totalDays)} j</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-lg font-black text-emerald-300" data-testid={`offre-facing-montant-${o.id}`}>{formatMoney(o.montantMensuel)}<span className="text-[10px] text-slate-500">/mois</span></p>
                <p className="text-[10px] text-slate-500">≈ {formatMoney(imp.gainContrat)} sur le contrat</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border border-ink-700 bg-ink-900/40 px-2 py-1.5">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Score planogramme</p>
                <p className="font-mono text-slate-200">{imp.scoreAvant} → <b>{imp.scoreApres}</b> ({delta(imp.scoreAvant, imp.scoreApres)})</p>
              </div>
              <div className="rounded border border-ink-700 bg-ink-900/40 px-2 py-1.5">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Marge aux yeux (panier)</p>
                <p className="font-mono text-slate-200">{imp.panierAvant} % → <b>{imp.panierApres} %</b> ({delta(imp.panierAvant, imp.panierApres, ' pt')})</p>
              </div>
              <div className="rounded border border-ink-700 bg-ink-900/40 px-2 py-1.5">
                <p className="text-[9px] uppercase tracking-wider text-slate-500">Ventes {o.famNom}</p>
                <p className="font-mono text-slate-200">×{imp.multFamAvant.toFixed(2)} → <b>×{imp.multFamApres.toFixed(2)}</b></p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400">Arbitrage : {formatMoney(o.montantMensuel)}/mois garantis contre {o.facings} emplacements de l'étagère reine bloqués pour une famille imposée par le fournisseur. Les emplacements restent verrouillés jusqu'à la fin du contrat.</p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { refuser(o.id); toast('Offre déclinée'); }} className="px-3 py-1.5 rounded text-xs font-bold border border-ink-600 text-slate-300 hover:bg-ink-800 flex items-center gap-1" data-testid={`offre-facing-refuser-${o.id}`}><X className="w-3.5 h-3.5" /> Refuser</button>
              <button type="button" onClick={() => { const res = accepter(o.id); if (res?.ok) toast('Tête de gondole acceptée', { description: `${o.facings} facings ${o.famNom} verrouillés au niveau des yeux · score ${res.score}/100 · ${formatMoney(o.montantMensuel)}/mois encaissés.` }); }} className="px-3 py-1.5 rounded text-xs font-bold bg-amber-500 text-ink-950 hover:bg-amber-400 flex items-center gap-1" data-testid={`offre-facing-accepter-${o.id}`}><Check className="w-3.5 h-3.5" /> Accepter</button>
            </div>
          </div>
        );
      })}

      {contrats.map(c => (
        <div key={c.id} className="rounded border border-ink-700 bg-ink-900/40 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]" data-testid={`contrat-facing-${c.id}`}>
          <Lock className="w-3.5 h-3.5 text-amber-300" />
          <span className="font-bold text-slate-200">{c.fournisseurNom}</span>
          <span className="text-slate-300">{c.facings} facings {c.famNom} (niveau des yeux)</span>
          <span className="font-mono text-emerald-300">{formatMoney(c.montantMensuel)}/mois</span>
          <span className="text-slate-500 ml-auto">reste {Math.max(0, c.finJour - totalDays)} j</span>
        </div>
      ))}
    </div>
  );
};
